import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar } from 'lucide-react';
import { generateServiceAreasListSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';

// City data for schema generation
const cities = [
  { name: 'Lexington', slug: 'lexington', county: 'Fayette County' },
  { name: 'Nicholasville', slug: 'nicholasville', county: 'Jessamine County' },
  { name: 'Danville', slug: 'danville', county: 'Boyle County' },
  { name: 'Richmond', slug: 'richmond', county: 'Madison County' },
  { name: 'Paris', slug: 'paris', county: 'Bourbon County' },
  { name: 'Harrodsburg', slug: 'harrodsburg', county: 'Mercer County' },
  { name: 'Winchester', slug: 'winchester', county: 'Clark County' },
  { name: 'Georgetown', slug: 'georgetown', county: 'Scott County' },
  { name: 'Midway', slug: 'midway', county: 'Woodford County' },
  { name: 'Lawrenceburg', slug: 'lawrenceburg', county: 'Anderson County' },
  { name: 'Versailles', slug: 'versailles', county: 'Woodford County' },
  { name: 'Frankfort', slug: 'frankfort', county: 'Franklin County' },
  { name: 'Lancaster', slug: 'lancaster', county: 'Garrard County' },
  { name: 'Cynthiana', slug: 'cynthiana', county: 'Harrison County' },
  { name: 'Mt Sterling', slug: 'mt-sterling', county: 'Montgomery County' },
  { name: 'Wilmore', slug: 'wilmore', county: 'Jessamine County' },
  { name: 'Stamping Ground', slug: 'stamping-ground', county: 'Scott County' }
];

const ServiceAreasPage = () => {
  // Generate schemas
  const itemListSchema = generateServiceAreasListSchema(cities);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Service Areas', url: 'https://comfortmasterheating.com/service-areas' }
  ]);

  // Inject schemas into document head
  useSchemaMarkup([itemListSchema, breadcrumbSchema], 'service-areas');

  return (
    <main>
      <Helmet>
        <title>Service Areas | HVAC Services Central Kentucky | ComfortMaster</title>
        <meta name="description" content="ComfortMaster Heating & Air serves 17+ cities in Central Kentucky including Lexington, Nicholasville, Georgetown, Richmond, Frankfort. Find HVAC services in your area." />
        <link rel="canonical" href="https://comfortmasterheating.com/service-areas" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Service Areas</span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl mt-4 mb-6">
              Areas We Serve in Central Kentucky
            </h1>
            <p className="text-xl text-stone-300 mb-8">
              ComfortMaster Heating & Air proudly serves homeowners and businesses throughout Central Kentucky. Find your city below for local HVAC services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="areas-estimate-btn">
                <Calendar size={20} />
                Get Free Estimate
              </Link>
              <a href="tel:8593576803" className="btn-secondary inline-flex items-center justify-center gap-2 bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900" data-testid="areas-call-btn">
                <Phone size={20} />
                (859) 357-6803
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">Select Your City</h2>
            <p className="text-slate-600">Click on your city to learn more about our local HVAC services</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Link to="/service-areas/lexington" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-lexington">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Lexington, KY</h3>
                  <p className="text-sm text-slate-500">Fayette County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/nicholasville" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-nicholasville">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Nicholasville, KY</h3>
                  <p className="text-sm text-slate-500">Jessamine County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/danville" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-danville">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Danville, KY</h3>
                  <p className="text-sm text-slate-500">Boyle County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/richmond" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-richmond">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Richmond, KY</h3>
                  <p className="text-sm text-slate-500">Madison County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/paris" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-paris">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Paris, KY</h3>
                  <p className="text-sm text-slate-500">Bourbon County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/harrodsburg" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-harrodsburg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Harrodsburg, KY</h3>
                  <p className="text-sm text-slate-500">Mercer County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/winchester" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-winchester">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Winchester, KY</h3>
                  <p className="text-sm text-slate-500">Clark County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/georgetown" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-georgetown">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Georgetown, KY</h3>
                  <p className="text-sm text-slate-500">Scott County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/midway" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-midway">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Midway, KY</h3>
                  <p className="text-sm text-slate-500">Woodford County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/lawrenceburg" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-lawrenceburg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Lawrenceburg, KY</h3>
                  <p className="text-sm text-slate-500">Anderson County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/versailles" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-versailles">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Versailles, KY</h3>
                  <p className="text-sm text-slate-500">Woodford County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/frankfort" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-frankfort">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Frankfort, KY</h3>
                  <p className="text-sm text-slate-500">Franklin County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/lancaster" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-lancaster">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Lancaster, KY</h3>
                  <p className="text-sm text-slate-500">Garrard County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/cynthiana" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-cynthiana">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Cynthiana, KY</h3>
                  <p className="text-sm text-slate-500">Harrison County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/mt-sterling" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-mt-sterling">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Mt Sterling, KY</h3>
                  <p className="text-sm text-slate-500">Montgomery County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/wilmore" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-wilmore">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Wilmore, KY</h3>
                  <p className="text-sm text-slate-500">Jessamine County</p>
                </div>
              </div>
            </Link>
            <Link to="/service-areas/stamping-ground" className="bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid="city-stamping-ground">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <MapPin className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-emerald-600 transition-colors">Stamping Ground, KY</h3>
                  <p className="text-sm text-slate-500">Scott County</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">Do Not See Your City?</h2>
          <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
            We serve many additional areas in Central Kentucky. Call us to confirm service availability in your location.
          </p>
          <a href="tel:8593576803" className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold py-3 px-6 rounded-full" data-testid="areas-cta-btn">
            <Phone size={18} />
            Call (859) 357-6803
          </a>
        </div>
      </section>
    </main>
  );
};

export default ServiceAreasPage;
